import {
  ArtifactType,
  ArtifactStartInfo,
  ParsedArtifactData,
  ArtifactParseResult,
} from "@/types/Artifact";

// Constants
const ARTIFACT_REGEX = {
  FULL: /(?:<SlatePanel\s[^>]*?type="([^"]+)"[^>]*?>([^]*?)<\/SlatePanel>)|(?:```visualization-json\n([^]*?)```)/g,
  START:
    /(?:<SlatePanel\s[^>]*?type="([^"]+)"[^>]*?)|(?:```visualization-json)/g,
  PARTIAL:
    /(?:<SlatePanel\s[^>]*?type="([^"]+)"[^>]*?>([^]*?)$)|(?:```visualization-json\n([^]*?)$)/,
  STREAMING:
    /(?:<SlatePanel\s[^>]*?type="([^"]+)"[^>]*?>)|(?:```visualization-json)/g,
};

const DEFAULT_TITLES: Record<ArtifactType, string> = {
  code: "Code Block",
  graph: "Chart",
  table: "Data Table",
  sql: "SQL Query",
  visualization: "Visualization",
  map: "Map",
  link: "Link",
};

/**
 * Generates default title for artifact type
 */
export const generateDefaultTitle = (type: ArtifactType): string => {
  return DEFAULT_TITLES[type] || "Artifact";
};

/**
 * Extracts attribute value from SlatePanel tag
 */
const extractAttribute = (
  tag: string,
  attributeName: string
): string | undefined => {
  const regex = new RegExp(`${attributeName}="([^"]+)"`, "i");
  const match = tag.match(regex);
  return match ? match[1] : undefined;
};

/**
 * Detects the start of artifacts in streaming content
 * This function is optimized to detect artifacts as soon as the opening tag appears
 */
export const detectArtifactStart = (content: string): ArtifactStartInfo[] => {
  const starts: ArtifactStartInfo[] = [];
  const seenArtifacts = new Set<string>();

  // Use streaming regex for immediate detection
  let match;
  ARTIFACT_REGEX.STREAMING.lastIndex = 0;

  while ((match = ARTIFACT_REGEX.STREAMING.exec(content)) !== null) {
    const [fullMatch, type] = match;

    let artifactType = type as ArtifactType;
    let artifactTitle = "";
    let artifactLanguage: string | undefined;

    if (fullMatch.startsWith("```visualization-json")) {
      artifactType = "visualization" as ArtifactType;
      artifactTitle = "Visualization"; // Default title
    } else {
      // Extract title and language from the full match (SlatePanel)
      const title = extractAttribute(fullMatch, "title");
      const language = extractAttribute(fullMatch, "language");
      artifactTitle = title || generateDefaultTitle(artifactType);
      artifactLanguage = language;
    }

    const finalTitle = artifactTitle;
    const artifactKey = `${artifactType}::${finalTitle}`;

    // Prevent duplicates
    if (!seenArtifacts.has(artifactKey)) {
      seenArtifacts.add(artifactKey);
      starts.push({
        type: artifactType,
        language: artifactLanguage || undefined,
        title: finalTitle,
      });
    }
  }

  return starts;
};

/**
 * Parses artifact content based on type
 */
export const parseArtifactContent = (
  type: ArtifactType,
  rawContent: string
): any => {
  const content = rawContent.trim();

  switch (type) {
    case "code":
      return content;

    case "sql":
      // Handle SQL with results
      if (content.includes("-- Result")) {
        const [query, resultPart] = content.split("-- Result");
        try {
          const result = JSON.parse(resultPart.trim());
          return { query: query.trim(), result };
        } catch {
          return { query: query.trim(), result: resultPart.trim() };
        }
      }
      return content;

    case "graph":
    case "visualization": // Both graph and visualization are JSON
    case "map": // Map data is also JSON
      try {
        return JSON.parse(content);
      } catch {
        // For streaming, return the raw content
        return content;
      }

    case "table":
      try {
        // First try parsing as standard JSON
        return JSON.parse(content);
      } catch {
        // Try parsing as multiple JSON arrays (SlatePanel format)
        try {
          const arrays: any[] = [];
          let currentPos = 0;

          while (currentPos < content.length) {
            // Find start of next array
            const startBracket = content.indexOf("[", currentPos);
            if (startBracket === -1) break;

            // Find matching end bracket
            let endBracket = startBracket + 1;
            let bracketCount = 1;

            while (bracketCount > 0 && endBracket < content.length) {
              if (content[endBracket] === "[") bracketCount++;
              else if (content[endBracket] === "]") bracketCount--;
              endBracket++;
            }

            const arrayStr = content.substring(startBracket, endBracket);
            const parsed = JSON.parse(arrayStr);
            arrays.push(parsed);

            currentPos = endBracket;
          }

          // Convert to table format: first array is headers, rest are rows
          if (arrays.length > 0) {
            return {
              headers: arrays[0],
              rows: arrays.slice(1),
            };
          }

          return content;
        } catch {
          // For streaming, return the raw content
          return content;
        }
      }

    default:
      return content;
  }
};

/**
 * Parses content for artifact elements and extracts them
 */
export const parseArtifactElements = (content: string): ArtifactParseResult => {
  if (!content?.trim()) {
    return { processedContent: content, artifacts: [] };
  }

  let processedContent = content;
  const artifacts: ParsedArtifactData[] = [];
  const seenArtifacts = new Set<string>();
  let match;

  // Reset regex lastIndex to ensure proper matching
  ARTIFACT_REGEX.FULL.lastIndex = 0;

  while ((match = ARTIFACT_REGEX.FULL.exec(content)) !== null) {
    // match[0] is the full matched string
    // match[1] is type for SlatePanel, match[2] is content for SlatePanel
    // match[3] is content for visualization-json

    const fullMatch = match[0];
    let type: string;
    let rawContent: string;
    let title: string | undefined;
    let language: string | undefined;

    if (fullMatch.startsWith("```visualization-json")) {
      // It's a JSON block
      rawContent = match[3] || ""; // Content is in the third capturing group for JSON block

      // Try to peek at the JSON to get the type and title
      try {
        const parsed = JSON.parse(rawContent);
        type = parsed.type || "visualization";
        title = parsed.title;
      } catch {
        type = "visualization";
        title = "Visualization";
      }
    } else {
      // SlatePanel
      type = match[1]; // Type is in the first capturing group for SlatePanel
      rawContent = match[2]; // Content is in the second capturing group for SlatePanel

      title = extractAttribute(fullMatch, "title");
      language = extractAttribute(fullMatch, "language");
    }

    const finalTitle = title || generateDefaultTitle(type as ArtifactType);
    const artifactKey = `${type}::${finalTitle}`;

    // Skip if we've already processed this artifact
    if (seenArtifacts.has(artifactKey)) {
      continue;
    }

    seenArtifacts.add(artifactKey);

    try {
      const parsedContent = parseArtifactContent(
        type as ArtifactType,
        rawContent
      );

      // Create artifact data with language preserved
      const artifactData: ParsedArtifactData = {
        type: type as ArtifactType,
        title: finalTitle,
        content: parsedContent,
      };

      // Add language property for code artifacts
      if (language) {
        artifactData.language = language;
      }

      artifacts.push(artifactData);

      // Replace with placeholder for UI rendering
      const placeholder = `[[ARTIFACT_PLACEHOLDER_${artifacts.length - 1}]]`;
      processedContent = processedContent.replace(fullMatch, placeholder);
    } catch (error) {
      console.warn("Failed to parse artifact content:", error);
      // Keep original content if parsing fails
    }
  }

  return { processedContent, artifacts };
};

/**
 * Extracts partial artifact from streaming content
 */
export const extractPartialArtifact = (
  content: string
): ParsedArtifactData | null => {
  const match = content.match(ARTIFACT_REGEX.PARTIAL);

  if (!match) return null;

  const fullMatch = match[0];
  let type: string;
  let partialContent: string;
  let title: string | undefined;
  let language: string | undefined;

  if (fullMatch.startsWith("```visualization-json")) {
    type = "visualization";
    partialContent = match[3] || ""; // Content is in the third capturing group for JSON block
    // Try to extract title from partial JSON if possible, otherwise default
    // This is hard for streaming JSON, so we might just use default
    title = "Visualization";
  } else {
    type = match[1]; // Type is in the first capturing group for SlatePanel
    partialContent = match[2] || ""; // Content is in the second capturing group for SlatePanel

    title = extractAttribute(fullMatch, "title");
    language = extractAttribute(fullMatch, "language");
  }

  const artifactData: ParsedArtifactData = {
    type: type as ArtifactType,
    title: title || generateDefaultTitle(type as ArtifactType),
    content: partialContent || "",
  };

  if (language) {
    artifactData.language = language;
  }

  return artifactData;
};

/**
 * Extracts streaming content for a specific artifact
 * This is optimized to extract content even if the closing tag is missing
 */
export const extractStreamingArtifactContent = (
  content: string,
  artifactType: ArtifactType,
  artifactTitle: string
): { content: string; language?: string } | null => {
  if (
    artifactType === "visualization" &&
    content.includes("```visualization-json")
  ) {
    // Handle JSON block streaming
    const regex = /```visualization-json\n([^]*?)(?:```|$)/;
    const match = content.match(regex);
    if (match) {
      return { content: match[1].trim() };
    }
    return null;
  }

  const escapedTitle = artifactTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Try to find the artifact with or without closing tag
  const artifactRegex = new RegExp(
    `<SlatePanel\\s[^>]*?type="${artifactType}"[^>]*?title="${escapedTitle}"[^>]*?>([^]*?)(?:</SlatePanel>|$)`,
    "i"
  );

  const match = content.match(artifactRegex);
  if (!match) {
    // Try without title match in case title is still streaming
    const fallbackRegex = new RegExp(
      `<SlatePanel\\s[^>]*?type="${artifactType}"[^>]*?>([^]*?)(?:</SlatePanel>|$)`,
      "i"
    );
    const fallbackMatch = content.match(fallbackRegex);
    if (fallbackMatch) {
      const [fullMatch, artifactContent] = fallbackMatch;
      const language = extractAttribute(fullMatch, "language");
      return {
        content: artifactContent?.trim() || "",
        language: language || undefined,
      };
    }

    return null;
  }

  const [fullMatch, artifactContent] = match;
  const language = extractAttribute(fullMatch, "language");

  return {
    content: artifactContent?.trim() || "",
    language: language || undefined,
  };
};

/**
 * Replaces artifact placeholders with interactive buttons
 */
export const renderArtifactPlaceholders = (
  content: string,
  artifacts: ParsedArtifactData[],
  messageId?: string
): string => {
  let result = content;

  artifacts.forEach((artifact, index) => {
    const placeholder = `[[ARTIFACT_PLACEHOLDER_${index}]]`;
    // Create stable button ID based on type and title
    const stableId = messageId
      ? `${messageId}_${artifact.type}_${artifact.title.replace(/\s+/g, "_")}`
      : `${Date.now()}_${index}`;
    const button = `[[ARTIFACT_BTN_${stableId}|${artifact.type}|${artifact.title}]]`;
    result = result.replace(placeholder, button);
  });

  return result;
};

/**
 * Renders loading artifact placeholders
 */
export const renderLoadingArtifactPlaceholders = (
  content: string,
  loadingArtifacts: Array<{ type: string; title: string; index: number }>
): string => {
  let result = content;

  loadingArtifacts.forEach((artifact) => {
    const loadingButton = `[[LOADING_ARTIFACT_BTN_${artifact.index}|${artifact.type}|${artifact.title}]]`;
    // Insert at the end of content for now
    result += `\n\n${loadingButton}`;
  });

  return result;
};

/**
 * Checks if content contains streaming artifact markers
 */
export const hasStreamingArtifacts = (content: string): boolean => {
  return (
    /<SlatePanel\s[^>]*?type="[^"]+"/g.test(content) ||
    /```visualization-json/g.test(content)
  );
};

/**
 * Processes streaming content to maintain proper structure
 * Replaces artifact tags with placeholders while preserving spacing
 */
export const processStreamingContent = (
  content: string,
  artifactReplacer: (
    type: string,
    language: string | undefined,
    title: string | undefined,
    index: number
  ) => string
): string => {
  let artifactIndex = 0;

  // Replace artifacts with proper spacing preservation
  const processed = content.replace(
    /(<SlatePanel\s[^>]*?type="([^"]+)"[^>]*?>([^]*?)(?:<\/SlatePanel>|$))|(```visualization-json\n([^]*?)(?:```|$))/g,
    (
      match,
      slatePanelFullMatch,
      slatePanelType,
      _slatePanelContent,
      jsonFullMatch,
      jsonContent,
      offset
    ) => {
      let type: string;
      let title: string | undefined;
      let language: string | undefined;
      let currentFullMatch: string;

      if (slatePanelFullMatch) {
        currentFullMatch = slatePanelFullMatch;
        type = slatePanelType;
        title = extractAttribute(currentFullMatch, "title");
        language = extractAttribute(currentFullMatch, "language");
      } else if (jsonFullMatch) {
        currentFullMatch = jsonFullMatch;
        type = "visualization";
        // Try to peek at the JSON to get the title
        try {
          const parsed = JSON.parse(jsonContent);
          title = parsed.title;
        } catch {
          title = "Visualization";
        }
      } else {
        // Should not happen with the regex structure
        return match;
      }

      const replacement = artifactReplacer(
        type,
        language,
        title,
        artifactIndex
      );
      artifactIndex++;

      // Check if we need to preserve spacing
      const beforeChar = content[offset - 1];
      const afterMatch =
        currentFullMatch.endsWith("</SlatePanel>") ||
        currentFullMatch.endsWith("```");

      let spacingBefore = "";
      let spacingAfter = "";

      // Add spacing before if needed
      if (beforeChar && beforeChar !== "\n") {
        spacingBefore = "\n\n";
      }

      // Add spacing after if there's content following
      if (afterMatch) {
        spacingAfter = "\n\n";
      }

      return `${spacingBefore}${replacement}${spacingAfter}`;
    }
  );

  // Clean up excessive newlines
  return processed.replace(/\n{3,}/g, "\n\n").trim();
};

/**
 * Filters out artifacts that should stream to panel from message content
 * Maintains proper spacing and structure
 */
export const filterStreamingArtifacts = (content: string): string => {
  // Remove SlatePanel tags
  let filtered = content.replace(
    /<SlatePanel\s[^>]*?type="[^"]+"[^>]*?>[\s\S]*?(?:<\/SlatePanel>|$)/g,
    ""
  );

  // Remove JSON blocks
  filtered = filtered.replace(/```visualization-json\n[\s\S]*?(?:```|$)/g, "");

  // Remove artifact button placeholders
  filtered = filtered.replace(/\[\[ARTIFACT_BTN_[^\]]+\]\]/g, "");
  filtered = filtered.replace(/\[\[ARTIFACT_PLACEHOLDER_\d+\]\]/g, "");

  // Clean up excessive whitespace
  return filtered.replace(/\n{3,}/g, "\n\n").trim();
};